"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { TURNSTILE_SITE_KEY } from "@/lib/sendForm";

type Turnstile = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global {
  interface Window {
    turnstile?: Turnstile;
    onTurnstileLoad?: () => void;
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad";

// Load the Turnstile script once, only on pages that have a form
let scriptPromise: Promise<Turnstile> | null = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise((resolve) => {
    window.onTurnstileLoad = () => resolve(window.turnstile!);
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export type FormGuardHandle = {
  /** Turnstile token + honeypot value to send with the form */
  get: () => { token: string; trap: string };
  /** Tokens are single-use — get a fresh one after each submit */
  reset: () => void;
};

/**
 * Bot protection for the site's forms: a Cloudflare Turnstile check (only shows
 * a checkbox when Cloudflare isn't sure about the visitor) plus a hidden
 * honeypot field that people never see but bots fill in.
 */
export const FormGuard = forwardRef<FormGuardHandle>(function FormGuard(_, ref) {
  const box = useRef<HTMLDivElement>(null);
  const trap = useRef<HTMLInputElement>(null);
  const widgetId = useRef<string | null>(null);
  const token = useRef("");

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !box.current) return;
      widgetId.current = ts.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        size: "flexible",
        appearance: "interaction-only",
        callback: (t: string) => (token.current = t),
        "expired-callback": () => (token.current = ""),
        "error-callback": () => (token.current = ""),
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    get: () => ({ token: token.current, trap: trap.current?.value ?? "" }),
    reset: () => {
      token.current = "";
      if (widgetId.current) window.turnstile?.reset(widgetId.current);
    },
  }));

  return (
    <>
      <div ref={box} className="empty:hidden" />
      {/* Honeypot — hidden from people and screen readers */}
      <input
        ref={trap}
        type="text"
        name="website_url"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />
    </>
  );
});
